import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Loader2 } from "lucide-react";
import { PageHero, Reveal, SectionHeading, usePageMeta } from "@/components/kit";
import { HERO_BG, PHONE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-[#0D0D10] border border-gold/20 rounded-sm px-4 py-3.5 text-sm text-[#F4EFE6] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors";

export default function PrayerRequestPage() {
  usePageMeta(
    "Prayer Requests | Redeemer Solutions LLC — Christiana, TN",
    "Send a prayer request to Redeemer Solutions LLC in Christiana, TN. Every request is read personally and prayed over."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", request: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { name: form.name, phone: form.phone, request: form.request };
      if (form.email) payload.email = form.email;
      await axios.post(`${API}/prayer-requests`, payload);
      toast.success("Your prayer request was received. We will be praying for you.");
      setForm({ name: "", email: "", phone: "", request: "" });
    } catch {
      toast.error(`Something went wrong. Please call us at ${PHONE}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="prayer-request-page">
      <PageHero
        testid="prayer-hero"
        eyebrow="Prayer Requests"
        lines={["How Can We", { t: "Pray for You?", gold: true }]}
        sub="Life gets heavy. Whatever you're carrying — family, health, work, worry — we'd be honored to lift it up in prayer."
        image={HERO_BG}
        imageAlt="Prayer requests at Redeemer Solutions LLC"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHeading
              eyebrow="You're Not Alone"
              title="Every request is read personally and prayed over."
              sub="Your request goes directly to Zach — never shared, never posted. Share as much or as little as you'd like; you don't even need to leave your name."
              testid="prayer-form-heading"
            />
            <Reveal delay={0.1} className="mt-10">
              <blockquote
                data-testid="prayer-scripture"
                className="border-l-2 border-gold/40 pl-6 py-1"
              >
                <p className="font-display text-lg sm:text-xl text-gold-champagne leading-relaxed">
                  "Do not be anxious about anything, but in every situation, by prayer and petition,
                  with thanksgiving, present your requests to God."
                </p>
                <cite className="block font-mono-label text-[11px] uppercase tracking-[0.22em] text-zinc-500 mt-4 not-italic">
                  Philippians 4:6
                </cite>
              </blockquote>
            </Reveal>
            <Reveal delay={0.16} className="mt-10">
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <HeartHandshake size={18} className="text-gold shrink-0" />
                <p>Prefer to talk? Call us at <span className="text-gold-champagne">{PHONE}</span> — we pray with folks over the phone too.</p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.08}>
            <form
              data-testid="prayer-form"
              onSubmit={submit}
              className="bg-[#121216] border border-gold/15 rounded-sm p-7 sm:p-9 space-y-5"
            >
              <div>
                <label htmlFor="prayer-name" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">
                  Name <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
                </label>
                <input id="prayer-name" data-testid="prayer-name-input" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name, or leave blank" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="prayer-email" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">
                    Email <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
                  </label>
                  <input id="prayer-email" data-testid="prayer-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="Only if you'd like a reply" />
                </div>
                <div>
                  <label htmlFor="prayer-phone" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">
                    Phone <span className="text-zinc-600 normal-case tracking-normal">(optional)</span>
                  </label>
                  <input id="prayer-phone" data-testid="prayer-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(615) 555-0123" />
                </div>
              </div>
              <div>
                <label htmlFor="prayer-request" className="block font-mono-label text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Prayer Request *</label>
                <textarea id="prayer-request" data-testid="prayer-request-input" required rows={6} value={form.request} onChange={set("request")} className={inputCls} placeholder="What can we pray about with you?" />
              </div>
              <button
                type="submit"
                data-testid="prayer-form-submit-button"
                disabled={sending}
                className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm tracking-wide px-10 py-4 rounded-sm transition-[transform,box-shadow,opacity] duration-300 hover:scale-[1.02] hover:shadow-[0_0_32px_rgba(212,175,55,0.35)] disabled:opacity-60 disabled:hover:scale-100"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                {sending ? "Sending..." : "Send Prayer Request"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
